import React, { useState, useRef } from 'react';
import { faCalendar, faBell } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';

interface DatePickerButtonProps {
    type: 'date' | 'datetime';
    value: Dayjs | null;
    onChange: (value: Dayjs | null) => void;
    format?: string;
    title?: string;
    minDate?: Dayjs;
}

export const DatePickerButton: React.FC<DatePickerButtonProps> = ({
    type,
    value,
    onChange,
    format,
    title,
    minDate
}) => {
    const [showPicker, setShowPicker] = useState(false);
    const iconRef = useRef<HTMLButtonElement>(null);

    // Set default minDate based on type
    // For datetime, use start of day to allow selecting today, and rely on disablePast for time validation
    const defaultMinDate = dayjs().startOf('day');
    const effectiveMinDate = minDate !== undefined ? minDate : defaultMinDate;

    const defaultFormat = type === 'date' ? 'd MMM, yyyy' : 'd MMM, yyyy, HH:mm';
    const icon = type === 'date' ? faCalendar : faBell;
    const defaultTitle = type === 'date' ? 'Due date' : 'Reminder';

    const formatDate = (date: Dayjs) => {
        if (!date) return '';
        return date.format('D MMM, YYYY');
    };

    const formatDateTime = (date: Dayjs) => {
        if (!date) return '';
        return date.format('D MMM, YYYY, HH:mm');
    };

    const PickerComponent = type === 'date' ? DatePicker : DateTimePicker;

    const handleChange = (newValue: Dayjs | null) => {
        if (type === 'datetime' && newValue) {
            const now = dayjs();
            const isToday = newValue.isSame(now, 'day');
            const isMidnight = newValue.hour() === 0 && newValue.minute() === 0;
            const isPast = newValue.isBefore(now);
            
            if (isToday && (isMidnight || isPast)) {
                onChange(now.add(1, 'minute'));
                return;
            }
        }
        onChange(newValue);
    };

    return (
        <div className="option-group">
            <button
                type="button"
                className={`option-icon ${value ? 'active' : ''}`}
                onClick={() => setShowPicker(true)}
                title={title || defaultTitle}
                ref={iconRef}
            >
                <FontAwesomeIcon icon={icon} />
            </button>
            <PickerComponent
                open={showPicker}
                onClose={() => setShowPicker(false)}
                value={value}
                onChange={handleChange}
                minDate={effectiveMinDate}
                timeSteps={{ minutes: 1 }}
                ampm={false}
                disablePast={type === 'datetime'}
                slots={{
                    textField: () => null,
                }}
                slotProps={{
                    popper: {
                        anchorEl: iconRef.current,
                    },
                }}
                format={format || defaultFormat}
            />
            {value && (
                <span className="option-text">
                    {type === 'date' ? formatDate(value) : formatDateTime(value)}
                </span>
            )}
        </div>
    );
};
